import { useQuery } from "@apollo/client";
import React, { createContext, ReactNode, useEffect, useState } from "react";
import { CURRENT_USER } from "../graphql/gqlUser";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  LinearProgress,
  Avatar,
} from "@mui/material";
import { darkTheme, defaultTheme } from "./colorContext";
import Clock from "@mui/icons-material/AccessTime";
import { User } from "../graphql/gqlTypes";

const uninitializedUser: User = {
  id: "",
  firstName: "",
  lastName: "",
  isAdmin: false,
  darkmode: false,
  language: "en",
};

export type UserContextType = {
  user: User;
  loading: boolean;
  refetch: any;
};

export const UserContext = createContext<UserContextType>(null);

type UserProviderProps = {
  children: ReactNode;
};

export const UserProvider = ({ children }: UserProviderProps) => {
  const darkmode = localStorage.getItem("darkmode");
  const theme = darkmode === "true" ? darkTheme : defaultTheme;

  const [user, setUser] = useState<UserContextType>({
    user: uninitializedUser,
    loading: true,
    refetch: () => {},
  });

  const { loading, error, data, refetch } = useQuery(CURRENT_USER, {
    pollInterval: 30000,
  });

  useEffect(() => {
    if (data?.getCurrentUser) {
      setUser({
        user: data.getCurrentUser,
        loading,
        refetch,
      });
      localStorage.setItem("darkmode", data.getCurrentUser.darkmode);
      localStorage.setItem("language", data.getCurrentUser.language);
    } else {
      setUser({
        user: uninitializedUser,
        loading,
        refetch,
      });
    }
  }, [data]);

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: theme.palette.background.default,
          height: "100vh",
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Container maxWidth="sm" sx={{ mb: 5 }}>
          <Paper elevation={6} sx={{ p: 4, borderRadius: 4 }}>
            <Stack spacing={3} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: "secondary.main",
                  width: 56,
                  height: 56,
                }}
              >
                <Clock fontSize="large" />
              </Avatar>

              <Typography variant="h4" fontWeight="bold">
                SjorCRAFT
              </Typography>

              <Typography
                variant="body1"
                fontWeight={600}
                textAlign="center"
                sx={{ color: theme.palette.primary.contrastText }}
              >
                Loading... Because we are using the free plan from render this
                can take a minute. In the meantime you can watch this
                instructional video about knotting.
              </Typography>

              <LinearProgress sx={{ width: "100%" }} />

              <Box
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 3,
                  aspectRatio: "16 / 9",
                }}
              >
                <Box
                  component="iframe"
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/aMxcAaR0oHU?si=WR_y8XdxZDC_deR2"
                  title="YouTube video player"
                  allow="web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  sx={{ border: 0 }}
                />
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};
